import React, { useState, useRef, useEffect, ReactNode } from "react";
import { useCookies } from "react-cookie";
import {
  GlobalStyles,
  VideoWrapper,
  VideoFrame,
  ProgressBar,
  Progress,
  BufferedProgress,
  Bottom,
  VideoActions,
  Play,
  Volume,
  Screen,
  Timer,
  Title,
} from "./styles";

import PlayIcon from "./assets/icons/Play";
import PauseIcon from "./assets/icons/Pause";
import CastIcon from "./assets/icons/Cast";
import CollapseIcon from "./assets/icons/Collapse";
import ExpandIcon from "./assets/icons/Expand";
import VolumeOffIcon from "./assets/icons/VolumeOff";
import VolumeOnIcon from "./assets/icons/VolumeOn";

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  const formattedTime = `${minutes}:${
    remainingSeconds < 10 ? "0" : ""
  }${remainingSeconds}`;
  return formattedTime;
};

interface ComponentProps {
  videoSource: string;
  title: string | ReactNode;
}

const VideoPlayer = ({ videoSource, title = "" }: ComponentProps) => {
  const [cookies, setCookie] = useCookies(["videoProgress"]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [bufferedProgress, setBufferedProgress] = useState(0);
  const [actionsVisible, setActionsVisible] = useState(false);
  const [currentTime, setCurrentTime] = useState("0:00");
  const [totalTime, setTotalTime] = useState("0:00");

  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }

      setIsPlaying(!isPlaying);
      // Set timeout to hide actions after 3 seconds
      setTimeout(() => {
        setActionsVisible(false);
      }, 2000);
    }
  };

  const handleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };

  // const handleFullScreen = () => {
  //   const video = videoRef.current;

  //   if (video) {
  //     if (!isFullScreen) {
  //       video.requestFullscreen();
  //     } else {
  //       document.exitFullscreen();
  //     }
  //   }
  // };

  const handleFullScreen = () => {
    const videoWrapper = videoWrapperRef.current;

    // Toggle the fullscreen state
    setIsFullScreen(!isFullScreen);

    // Update UI based on the fullscreen state
    if (videoWrapper) {
      videoWrapper.classList.toggle("fullscreen", !isFullScreen);
    }

    // if (video) {
    //   if (!isFullScreen) {
    //     if (video.requestFullscreen) {
    //       video.requestFullscreen();
    //     } else if (video.mozRequestFullScreen) {
    //       video.mozRequestFullScreen();
    //     } else if (video.webkitRequestFullScreen) {
    //       video.webkitRequestFullScreen();
    //     }
    //   } else {
    //     if (document.exitFullscreen) {
    //       document.exitFullscreen();
    //     } else if (document.mozCancelFullScreen) {
    //       document.mozCancelFullScreen();
    //     } else if (document.webkitIsFullScreen) {
    //       document.webkitIsFullScreen();
    //     }
    //   }

    // }
  };

  const handleProgress = () => {
    const video = videoRef.current;
    if (video) {
      const bufferedEnd = video.buffered.end(0); // Get the end time of the buffered range
      const bufferedPercentage = (bufferedEnd / video.duration) * 100;
      setBufferedProgress(bufferedPercentage);

      const currentPercentage = (video.currentTime / video.duration) * 100;
      setProgress(currentPercentage);

      const formattedCurrentTime = formatTime(video.currentTime);
      setCurrentTime(formattedCurrentTime);

      const formattedTotalTime = formatTime(video.duration);
      setTotalTime(formattedTotalTime);
    }
  };

  const handleProgressBarClick = (event) => {
    const video = videoRef.current;
    if (video) {
      const progressBar = event.currentTarget;
      const clickPosition =
        event.clientX - progressBar.getBoundingClientRect().left;
      const percentageClicked = (clickPosition / progressBar.clientWidth) * 100;
      const timeToSeek = (percentageClicked / 100) * video.duration;

      video.currentTime = timeToSeek;
    }
  };

  const handleDragStart = (event) => {
    // Set data to identify the drag operation
    event.dataTransfer.setData("text/plain", "progress-bar-drag");
  };

  const handleDragOver = (event) => {
    // Allow drop events
    event.preventDefault();
  };

  const handleDrop = (event) => {
    // Prevent default drop behavior
    event.preventDefault();

    const video = videoRef.current;
    const progressBar = progressBarRef.current;

    if (video && progressBar) {
      // Calculate the new progress based on the drop position
      const clickX = event.clientX - progressBar.getBoundingClientRect().left;
      const progressPercentage = (clickX / progressBar.offsetWidth) * 100;

      // Update progress and seek the video
      setProgress(progressPercentage);
      const newTime = (progressPercentage / 100) * video.duration;
      video.currentTime = newTime;
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.addEventListener("timeupdate", handleProgress);
      return () => {
        video.removeEventListener("timeupdate", handleProgress);
      };
    }
  }, []);

  useEffect(() => {
    if (actionsVisible && isPlaying) {
      setTimeout(() => {
        setActionsVisible(false);
      }, 2000);
    }
  }, [actionsVisible]);

  return (
    <>
      {isFullScreen && <GlobalStyles />}
      <VideoWrapper
        ref={videoWrapperRef}
        onClick={() => setActionsVisible(true)}
      >
        <VideoFrame>
          <video
            ref={videoRef}
            src={videoSource}
            muted={isMuted}
            style={{ width: "100%", height: "auto" }}
            controlsList=""
          />
          <VideoActions
            className={actionsVisible || !isPlaying ? "visible" : ""}
          >
            <Title>{title}</Title>
            <Play onClick={handlePlayPause}>
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </Play>
            <Volume onClick={handleMute}>
              {isMuted ? <VolumeOffIcon /> : <VolumeOnIcon />}
            </Volume>
            <Screen onClick={handleFullScreen}>
              {isFullScreen ? <CollapseIcon /> : <ExpandIcon />}
            </Screen>
            <ProgressBar
              onClick={handleProgressBarClick}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <Bottom
                ref={progressBarRef}
                progress={progress}
                draggable="true"
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              />
              <Progress progress={progress} />
              <BufferedProgress
                bufferedProgress={bufferedProgress} // Pass buffered progress to the Progress component
              />
            </ProgressBar>
            <Timer>
              {currentTime} / {totalTime}
            </Timer>
          </VideoActions>
        </VideoFrame>
      </VideoWrapper>
    </>
  );
};

export default VideoPlayer;
